// SEP-2 / E0 — ConsoleSection: the shared four-state scaffold every owner-workspace console sits on.
// Lives in the creator App Detail (owner side of the owner/customer boundary), NEVER in a generated
// customer app. It centralizes the HONEST loading / unavailable / error / ready-empty / ready-list
// states so no console can drift into a fake empty (a 503 must read "not configured yet", never an
// empty list) and every console is a real labelled landmark (<section aria-label>).
//
// Category-agnostic on purpose: Shop (products/inventory/orders/CRM) AND Booking (services/customers)
// consume the same scaffold. It renders ZERO business logic — the caller passes state + copy keys and
// owns the ready-list content. All strings are i18n KEYS resolved via t() at render (never literals).
import type { JSX, ComponentChildren } from 'preact';
import { RotateCw, Inbox } from 'lucide-preact';
import { t } from '../lib/i18n';

/** The honest load state a console is in. Deliberately mirrors the proven ManageRecords `Load` union
 *  so an existing console can adopt this scaffold without changing its fetch code. */
export type ConsoleState =
  | { status: 'loading' }
  | { status: 'unavailable' } // server store not configured (503) — NEVER shown as an empty list
  | { status: 'error'; code?: number }
  | { status: 'ready' };

export interface ConsoleSectionProps {
  /** i18n key for the section's accessible name + visible heading. */
  titleKey: string;
  /** leading icon (decorative). */
  icon?: JSX.Element;
  /** count badge shown next to the title when ready. */
  count?: number;
  /** current honest load state. */
  state: ConsoleState;
  /** true when state.status==='ready' but there are no rows → the honest empty state. */
  isEmpty?: boolean;
  /** retry handler for the error state + the header refresh button. */
  onRetry?: () => void;
  /** when set, a header Refresh button is shown (disabled while loading). */
  onRefresh?: () => void;
  /** extra header controls on the trailing side (e.g. an Add button, a CSV export link). */
  headerActions?: ComponentChildren;
  /** ready-list content (rendered only when ready + !isEmpty). */
  children?: ComponentChildren;

  // ── copy keys (all optional; sensible generic defaults) ────────────────
  emptyTitleKey?: string;
  emptyBodyKey?: string;
  emptyIcon?: JSX.Element;
  errorTitleKey?: string;
  errorAuthKey?: string;   // shown when error code === 401
  errorGenericKey?: string;
  unavailableTitleKey?: string;
  unavailableBodyKey?: string;
  /** skeleton row count while loading. */
  skeletonRows?: number;
}

export function ConsoleSection({
  titleKey, icon, count, state, isEmpty, onRetry, onRefresh, headerActions, children,
  emptyTitleKey = 'own.emptyT', emptyBodyKey = 'own.emptyS', emptyIcon,
  errorTitleKey = 'own.errT', errorAuthKey = 'own.errAuth', errorGenericKey = 'own.errGeneric',
  unavailableTitleKey = 'own.unavailableT', unavailableBodyKey = 'own.unavailableS',
  skeletonRows = 2,
}: ConsoleSectionProps): JSX.Element {
  const busy = state.status === 'loading';
  return (
    <section
      aria-label={t(titleKey)}
      aria-busy={busy ? 'true' : 'false'}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {icon}
          {t(titleKey)}
          {state.status === 'ready' && typeof count === 'number' && (
            <span className="pf-badge" style={{ marginInlineStart: 6 }}>{count}</span>
          )}
        </span>
        <span style={{ display: 'inline-flex', gap: 7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {headerActions}
          {onRefresh && (
            <button type="button" className="pf-btn pf-btn--sm" onClick={onRefresh} disabled={busy}>
              <RotateCw size={13} /> {t('mgmt.refresh')}
            </button>
          )}
        </span>
      </div>

      {state.status === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
          {Array.from({ length: skeletonRows }, (_, i) => (
            <div key={i} className="skeleton-shimmer" style={{ height: 60, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />
          ))}
        </div>
      )}

      {state.status === 'unavailable' && (
        <div className="pf-mgmt-panel" role="status">
          <span className="pf-mgmt-panel__body">
            <span className="pf-mgmt-panel__t">{t(unavailableTitleKey)}</span>
            <span className="pf-mgmt-panel__s">{t(unavailableBodyKey)}</span>
          </span>
        </div>
      )}

      {state.status === 'error' && (
        <div className="pf-mgmt-panel" role="alert">
          <span className="pf-mgmt-panel__body">
            <span className="pf-mgmt-panel__t">{t(errorTitleKey)}</span>
            <span className="pf-mgmt-panel__s">{state.code === 401 ? t(errorAuthKey) : t(errorGenericKey)}</span>
          </span>
          {onRetry && (
            <button type="button" className="pf-btn pf-btn--sm" onClick={onRetry}>
              <RotateCw size={13} /> {t('common.retry')}
            </button>
          )}
        </div>
      )}

      {state.status === 'ready' && isEmpty && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true">{emptyIcon ?? <Inbox size={22} />}</span>
          <p className="pf-empty__title">{t(emptyTitleKey)}</p>
          <p className="pf-empty__lede">{t(emptyBodyKey)}</p>
        </div>
      )}

      {state.status === 'ready' && !isEmpty && children}
    </section>
  );
}
